import { createFileRoute, Link } from "@tanstack/react-router";
import { Building2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { UserDrawer } from "@/components/users/UserDrawer";
import { useAccountTree } from "@/lib/hooks/accounts";
import { useCurrentUser } from "@/lib/hooks/auth";
import type { AccountTreeNode } from "@/lib/schemas/accounts";
import { ROLE_LEVEL, type UserRole } from "@/lib/schemas/users";

export const Route = createFileRoute("/_app/users_/tree")({
  component: UsersTreePage,
});

// ADR-0105 Track B: the "Albero utenti" page is now the ACCOUNT hierarchy
// (reseller → customer), each account → departments → members ordered by
// privilege (the BE already sorts admin → member → viewer).
function UsersTreePage(): JSX.Element {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const myLevel = ROLE_LEVEL[(me.data?.role ?? "peer_read") as UserRole] ?? 0;
  const isAdmin = myLevel >= ROLE_LEVEL.admin;

  const [selectedId, setSelectedId] = useState<string | undefined>();
  const query = useAccountTree(isAdmin);

  // Assemble the reseller→customer forest from parent_account_id. A node whose
  // parent is outside the visible set is treated as a root.
  const { roots, childrenOf } = useMemo(() => {
    const nodes = query.data?.nodes ?? [];
    const ids = new Set(nodes.map((n) => n.id));
    const childrenOf = new Map<string, AccountTreeNode[]>();
    const roots: AccountTreeNode[] = [];
    for (const n of nodes) {
      if (n.parent_account_id && ids.has(n.parent_account_id)) {
        const arr = childrenOf.get(n.parent_account_id) ?? [];
        arr.push(n);
        childrenOf.set(n.parent_account_id, arr);
      } else {
        roots.push(n);
      }
    }
    return { roots, childrenOf };
  }, [query.data]);

  if (!me.isLoading && !isAdmin) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-semibold tracking-tight">{t("users_tree.title")}</h1>
        <div className="rounded-md border border-border bg-card p-6 text-sm text-muted-foreground">
          {t("users_tree.access_denied")}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-semibold tracking-tight">{t("users_tree.title")}</h1>
          <Link to="/accounts" className="text-sm text-muted-foreground underline-offset-2 hover:underline">
            {t("users_tree.back_to_accounts")} →
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">{t("users_tree.subtitle")}</p>
      </header>

      {query.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("users_tree.load_error")} — {query.error.message}
        </div>
      )}

      {query.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-5/6" />
          <Skeleton className="h-8 w-4/6" />
        </div>
      ) : roots.length === 0 ? (
        <div className="rounded-md border border-border bg-card p-6 text-sm text-muted-foreground">
          {t("users_tree.empty")}
        </div>
      ) : (
        <ul className="space-y-3">
          {roots.map((node) => (
            <AccountTreeItem
              key={node.id}
              node={node}
              childrenOf={childrenOf}
              onSelectUser={setSelectedId}
            />
          ))}
        </ul>
      )}

      <UserDrawer userId={selectedId} onClose={() => setSelectedId(undefined)} />
    </div>
  );
}

function AccountTreeItem({
  node,
  childrenOf,
  onSelectUser,
}: {
  node: AccountTreeNode;
  childrenOf: Map<string, AccountTreeNode[]>;
  onSelectUser: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const children = childrenOf.get(node.id) ?? [];
  return (
    <li className="rounded-md border border-border bg-card">
      <div className="flex items-center gap-2 px-3 py-2">
        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="font-medium">{node.name}</span>
        <Badge variant="outline">{t(`accounts.type.${node.account_type}`)}</Badge>
        {node.status !== "active" ? (
          <Badge variant="secondary" className="text-[10px]">
            {node.status}
          </Badge>
        ) : null}
      </div>

      <div className="space-y-2 border-t border-border px-3 py-2">
        {node.departments.map((dept) => (
          <div key={dept.id}>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{dept.name}</p>
            {dept.members.length === 0 ? (
              <p className="pl-2 text-xs text-muted-foreground">{t("users_tree.no_members")}</p>
            ) : (
              <ul className="mt-1 space-y-0.5">
                {dept.members.map((m, i) => {
                  const clickable = Boolean(m.user_id);
                  return (
                    <li
                      key={`${dept.id}-${m.user_id ?? m.invited_email}-${i}`}
                      className={
                        "flex items-center justify-between gap-2 rounded px-2 py-1 text-sm " +
                        (clickable ? "cursor-pointer hover:bg-accent" : "opacity-70")
                      }
                      onClick={clickable ? () => onSelectUser(m.user_id as string) : undefined}
                    >
                      <span className="truncate">
                        {m.display_name || m.invited_email}
                        {m.status === "invited" ? (
                          <span className="ml-1 text-xs text-muted-foreground">({t("users_tree.pending_invite")})</span>
                        ) : null}
                      </span>
                      <Badge variant="outline" className="shrink-0 text-[10px]">
                        {t(`accounts.role.${m.role}`)}
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </div>

      {children.length > 0 ? (
        <ul className="space-y-2 border-t border-border bg-muted/30 px-3 py-2 pl-6">
          {children.map((child) => (
            <AccountTreeItem key={child.id} node={child} childrenOf={childrenOf} onSelectUser={onSelectUser} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
